import {
  BuilderModuleVersionQuery,
  ModuleBuilderContentMembershipFragment,
} from "./ModuleDetailPageContainer.generated";
import { atom } from "jotai";
import { selectAtom } from "jotai/utils";
import { isEqual } from "lodash";
import { getMembershipSections } from "./utils/getMembershipSections";
import { ModuleValidationErrors } from "./contexts/ModuleBuilderContext";
import {
  ContentMembershipItemData,
  CourseContentMembershipItemData,
  courseOutlineContentMembershipItemData,
  FileContentMembershipItemData,
  getMembershipScreenId,
  ModuleMembershipItemData,
  ScormCourseContentMembershipItemData,
  SectionBreakMembershipItemData,
  SkillContentMembershipItemData,
} from "./hooks/useModuleMembershipItems";
import { BuilderActionContextContent } from "@src/features/libraryItem/contexts/BuilderActionContext";
import {
  emptySkillValidationErrors,
  skillValidationErrorCount,
} from "../skill/utils/validation";
import { emptyCourseValidationErrors } from "../course/types/validation";
import { courseValidationErrorCount } from "../course/utils/publishValidation";
import { emptyModuleValidationErrors } from "./utils/publishValidation";

export const moduleVersionAtom = atom<
  BuilderModuleVersionQuery["ModuleVersion"] | undefined
>(undefined);

/**
 * Content keys (`generationFailedKey`) whose AI generation failed during this
 * page session. The server `aiGenerationFailed` flag is sticky, so the
 * "Failed" tag renders from this in-memory set instead: it disappears on
 * reload, when the content regenerates or succeeds, or when the user edits it.
 */
export const sessionGenerationFailedKeysAtom = atom<ReadonlySet<string>>(
  new Set<string>(),
);
export const generationFailedKey = (
  kind: "course" | "skill",
  id: number,
): string => `${kind}:${id}`;

export const lastBuilderActionIdAtom = selectAtom(
  moduleVersionAtom,
  (val) => val?.module?.lastBuilderActionId,
  isEqual,
);
export const builderActionContentAtom = atom<BuilderActionContextContent[]>(
  (get) => {
    const ret: BuilderActionContextContent[] = [];
    const moduleVersionModuleId = get(moduleVersionModuleIdAtom);
    const lastBuilderActionId = get(lastBuilderActionIdAtom);
    if (moduleVersionModuleId) {
      ret.push({
        contentIds: { moduleId: moduleVersionModuleId },
        initialLastAppliedBuilderActionId: lastBuilderActionId,
      });
    }
    const moduleContentMemberships = get(moduleContentMembershipsAtom);
    moduleContentMemberships?.forEach((m) => {
      if (m.course) {
        ret.push({
          contentIds: { courseId: m.course.id },
          initialLastAppliedBuilderActionId: m.course.lastBuilderActionId,
        });
      } else if (m.skill) {
        ret.push({
          contentIds: { skillId: m.skill.id },
          initialLastAppliedBuilderActionId: m.skill.lastBuilderActionId,
        });
      } else if (m.trainingResource) {
        ret.push({
          contentIds: {
            trainingResourceId: m.trainingResource.id,
          },
          initialLastAppliedBuilderActionId:
            m.trainingResource.lastBuilderActionId,
        });
      }
    });
    return ret;
  },
);
export const moduleContentMembershipsAtom = selectAtom(
  moduleVersionAtom,
  (pv) => pv?.moduleContentMemberships,
  isEqual,
);
export const moduleVersionNameAtom = selectAtom(
  moduleVersionAtom,
  (pv) => pv?.module?.libraryItem?.name,
  isEqual,
);
export const moduleVersionDescriptionAtom = selectAtom(
  moduleVersionAtom,
  (pv) => pv?.module?.libraryItem?.description,
  isEqual,
);
export const moduleVersionIdAtom = selectAtom(
  moduleVersionAtom,
  (pv) => pv?.id,
  isEqual,
);

export const moduleVersionDescriptionEnAtom = selectAtom(
  moduleVersionDescriptionAtom,
  (val) => val?.en ?? "",
  isEqual,
);

export const moduleVersionCoverImageAtom = selectAtom(
  moduleVersionAtom,
  (pv) => pv?.module?.libraryItem?.coverImage,
  isEqual,
);

export const moduleVersionPublishStateAtom = selectAtom(
  moduleVersionAtom,
  (mv) => mv?.module?.libraryItem?.publishState,
);

export const moduleVersionNameEnAtom = selectAtom(
  moduleVersionNameAtom,
  (val) => val?.en,
  isEqual,
);
export const moduleVersionModuleAtom = selectAtom(
  moduleVersionAtom,
  (val) => val?.module,
  isEqual,
);

export const moduleVersionModuleIdAtom = selectAtom(
  moduleVersionModuleAtom,
  (val) => val?.id,
  isEqual,
);

export const moduleValidationErrorsAtom = atom<ModuleValidationErrors>(
  emptyModuleValidationErrors(),
);
export const draftVersionSelectedAtom = selectAtom(
  moduleVersionAtom,
  (moduleVersion) => {
    return moduleVersion?.id === moduleVersion?.module.draftVersion.id;
  },
  isEqual,
);
export const getModuleMembershipValidationErrorCount = (
  mem: ModuleBuilderContentMembershipFragment,
  moduleValidationErrors: ModuleValidationErrors,
): number => {
  let count = 0;
  if (mem.course) {
    const courseValidationErrors =
      moduleValidationErrors.courseIdToValidationErrors?.get(mem.course?.id) ||
      emptyCourseValidationErrors();
    count += courseValidationErrorCount(courseValidationErrors);
  } else if (mem.courseOutline) {
    const hasCourseOutlineErrors =
      moduleValidationErrors.courseOutlineIds.length > 0;
    count += hasCourseOutlineErrors ? 1 : 0;
  } else if (mem.skill) {
    const skillValidationErrors =
      moduleValidationErrors.skillIdToValidationErrors?.get(mem.skill?.id) ||
      emptySkillValidationErrors();
    count += skillValidationErrorCount(skillValidationErrors);
  }
  return count;
};

type Return = {
  membershipItems: ModuleMembershipItemData[];
};
export const moduleMembershipItemsAtom = atom<Return>((get) => {
  const moduleVersion = get(moduleVersionAtom);
  const validationErrors = get(moduleValidationErrorsAtom);
  const items: ModuleMembershipItemData[] = [];
  if (!moduleVersion) return { membershipItems: items };
  for (
    let memIndex = 0;
    memIndex < moduleVersion.moduleContentMemberships.length;
    memIndex++
  ) {
    const membership = moduleVersion.moduleContentMemberships[memIndex];
    const memValidationErrorCount = getModuleMembershipValidationErrorCount(
      membership,
      validationErrors || emptyModuleValidationErrors(),
    );
    if (membership.sectionBreak) {
      items.push(
        new SectionBreakMembershipItemData(
          getMembershipScreenId(membership.uuid),
          membership.fractionPosition,
          moduleVersion,
          membership,
          memValidationErrorCount,
          membership.sectionBreak,
        ),
      );
    } else {
      if (membership.course?.id) {
        items.push(
          new CourseContentMembershipItemData(
            getMembershipScreenId(membership.uuid),
            membership.fractionPosition,
            moduleVersion,
            membership,
            memValidationErrorCount,
            membership.course,
          ),
        );
      } else if (membership.trainingResource?.id) {
        items.push(
          new FileContentMembershipItemData(
            getMembershipScreenId(membership.uuid),
            membership.fractionPosition,
            moduleVersion,
            membership,
            memValidationErrorCount,
            membership.trainingResource,
          ),
        );
      } else if (membership.skill?.id) {
        items.push(
          new SkillContentMembershipItemData(
            getMembershipScreenId(membership.uuid),
            membership.fractionPosition,
            moduleVersion,
            membership,
            memValidationErrorCount,
            membership.skill,
          ),
        );
      } else if (membership.scormCourse?.id) {
        items.push(
          new ScormCourseContentMembershipItemData(
            getMembershipScreenId(membership.uuid),
            membership.fractionPosition,
            moduleVersion,
            membership,
            memValidationErrorCount,
            membership.scormCourse,
          ),
        );
      } else if (membership.courseOutline?.id) {
        items.push(
          new courseOutlineContentMembershipItemData(
            getMembershipScreenId(membership.uuid),
            membership.fractionPosition,
            moduleVersion,
            membership,
            memValidationErrorCount,
            membership.courseOutline,
          ),
        );
      }
    }
  }
  return {
    membershipItems: items,
  };
});
export const membershipItemsAtom = selectAtom(
  moduleMembershipItemsAtom,
  (val) => {
    return val?.membershipItems;
  },
  isEqual,
);
export const membershipSectionsAtom = selectAtom(
  membershipItemsAtom,
  (membershipItems) => {
    return getMembershipSections(membershipItems);
  },
  isEqual,
);
export const contentMembershipItemsAtom = selectAtom(
  membershipItemsAtom,
  (membershipItems) => {
    return membershipItems?.filter(
      (item): item is ContentMembershipItemData =>
        item instanceof ContentMembershipItemData,
    );
  },
  isEqual,
);
export const selectedMembershipUuidsAtom = atom<string[]>([]);
export const selectedMembershipItemsAtom = atom((get) => {
  const selectedMembershipUuids = get(selectedMembershipUuidsAtom);
  const membershipItems = get(
    selectAtom(
      moduleMembershipItemsAtom,
      (moduleMembershipItems) => moduleMembershipItems.membershipItems,
      isEqual,
    ),
  );
  return membershipItems.filter(
    (item): item is ContentMembershipItemData =>
      selectedMembershipUuids.includes(item.id) &&
      item instanceof ContentMembershipItemData,
  );
});
export const activeOutlineUuidAtom = atom<string | null>(null);
export const publishedVersionSelectedAtom = selectAtom(
  moduleVersionAtom,
  (moduleVersion) => {
    return (
      moduleVersion &&
      moduleVersion.id == moduleVersion?.module.publishedVersion?.id
    );
  },
);

/**
 * Bumped by the Overview's "Review & publish" on a file or link resource —
 * which has no builder to review in — and consumed by that resource's header,
 * which owns the publish flow. A counter rather than a flag, so every request
 * is seen even when the last one was declined.
 */
export const publishRequestedAtom = atom(0);
